import { Suspense, Component, ReactNode } from "react";
import { Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import usePageViewTracking from "@/hooks/use-page-view-tracking";
import { lazyWithRetry } from "@/utils/lazy-with-retry";
import Auth from "@/pages/Auth";

// Error boundary for lazy-loaded components
class LazyErrorBoundary extends Component<{ children: ReactNode }> {
  state = { hasError: false, error: null as any };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any) {
    console.error('[LazyErrorBoundary] Caught error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 p-4">
            <p className="text-red-500 font-semibold">Failed to load page</p>
            <p className="text-xs text-muted-foreground text-center max-w-md">{String(this.state.error)}</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-primary text-white rounded">
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load pages for better TTI.
// lazyWithRetry keeps a transient chunk fetch failure (Vite restart in dev,
// stale chunk hashes after a deploy) from permanently breaking the route.
const Index = lazyWithRetry(() => import("@/pages/Index"), "Index");
const Meetings = lazyWithRetry(() => import("@/pages/Meetings"), "Meetings");
const MeetingDetail = lazyWithRetry(() => import("@/pages/MeetingDetail"), "MeetingDetail");
const Participants = lazyWithRetry(() => import("@/pages/Participants"), "Participants");
const Analytics = lazyWithRetry(() => import("@/pages/Analytics"), "Analytics");
const Templates = lazyWithRetry(() => import("@/pages/Templates"), "Templates");
const Settings = lazyWithRetry(() => import("@/pages/Settings"), "Settings");
const Notifications = lazyWithRetry(() => import("@/pages/Notifications"), "Notifications");
const FixRecording = lazyWithRetry(() => import("@/pages/FixRecording"), "FixRecording");
const RecordingRecovery = lazyWithRetry(() => import("@/pages/RecordingRecovery"), "RecordingRecovery");
const StorageTest = lazyWithRetry(() => import("@/pages/StorageTest"), "StorageTest");
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"), "NotFound");

// Loading fallback component with better UX
const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

/** Suspense + a chunk-load error boundary around a lazily loaded page. */
const LazyPage = ({ children }: { children: ReactNode }) => (
  <LazyErrorBoundary>
    <Suspense fallback={<RouteLoadingFallback />}>
      {children}
    </Suspense>
  </LazyErrorBoundary>
);

const ProtectedPage = ({ children }: { children: ReactNode }) => (
  <ProtectedRoute>
    <LazyPage>{children}</LazyPage>
  </ProtectedRoute>
);

export const RoutesWithTracking = () => {
  // Track page views for GTM on route changes
  usePageViewTracking();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth" element={<Auth />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedPage><Index /></ProtectedPage>} />
      <Route path="/meetings" element={<ProtectedPage><Meetings /></ProtectedPage>} />
      <Route path="/meetings/:id" element={<ProtectedPage><MeetingDetail /></ProtectedPage>} />
      <Route path="/participants" element={<ProtectedPage><Participants /></ProtectedPage>} />
      <Route path="/analytics" element={<ProtectedPage><Analytics /></ProtectedPage>} />
      <Route path="/templates" element={<ProtectedPage><Templates /></ProtectedPage>} />
      <Route path="/settings" element={<ProtectedPage><Settings /></ProtectedPage>} />
      <Route path="/fix-recording" element={<ProtectedPage><FixRecording /></ProtectedPage>} />
      <Route path="/recovery" element={<ProtectedPage><RecordingRecovery /></ProtectedPage>} />
      <Route path="/storage-test" element={<ProtectedPage><StorageTest /></ProtectedPage>} />
      <Route path="/notifications" element={<ProtectedPage><Notifications /></ProtectedPage>} />
      <Route path="*" element={<LazyPage><NotFound /></LazyPage>} />
    </Routes>
  );
};
